import React, { useState } from 'react';
import { todayKST } from '../../../utils/date';

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 7,
  border: '1px solid var(--border)',
  fontSize: 12,
  background: 'var(--bg-card)',
  color: 'var(--text)',
  boxSizing: 'border-box',
  outline: 'none',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
const sectionBox = {
  background: 'var(--bg-subtle)',
  borderRadius: 10,
  padding: '12px 14px',
  border: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
  boxSizing: 'border-box',
};
const btnMini = {
  border: '1px solid var(--border)',
  borderRadius: 7,
  padding: '4px 8px',
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  fontWeight: 700,
  fontSize: 10,
  cursor: 'pointer',
};
const badgeStyle = (color, bg) => ({
  marginLeft: 6, fontSize: 9, fontWeight: 800, color, background: bg,
  borderRadius: 5, padding: '1px 6px', verticalAlign: 'middle',
});

const fmt = (n) => Number(n || 0).toLocaleString('ko-KR');
const yn = (v) => (v !== false ? '✓' : '✗');

function rowTitle(row) {
  const payType = row.pay_type === 'hourly' ? '시급' : '월급';
  return `${row.effective_from}부터 · ${payType} ${fmt(row.base_salary)}원`;
}

// 접힌 상태 요약 — 부양가족·보험 가짓수·수습을 노출해 "전체 스냅샷"임을 암시
function rowSub(row) {
  const insCount = [row.national_pension_yn, row.health_insurance_yn, row.employment_ins_yn]
    .filter((v) => v !== false).length;
  const unit = row.probation_period_unit === 'day' ? '일' : '개월';
  const probation = Number(row.probation_months) > 0
    ? `수습 ${row.probation_months}${unit} ${Math.round(Number(row.probation_ratio || 0.9) * 100)}%`
    : '수습 없음';
  return `부양 ${row.dependents_count ?? 1}명 · 보험 ${insCount}종 · ${probation}`;
}

// 펼친 상태 — 그 시점 전체 급여정보 스냅샷
function DetailRow({ label, children }) {
  return (
    <>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text)', minWidth: 0 }}>{children}</div>
    </>
  );
}

function HistoryDetail({ row }) {
  const items = Array.isArray(row.item_values) ? row.item_values : [];
  const unit = row.probation_period_unit === 'day' ? '일' : '개월';
  const probation = Number(row.probation_months) > 0
    ? `${row.probation_months}${unit} · 급여 ${Math.round(Number(row.probation_ratio || 0.9) * 100)}%`
    : '없음';
  const createdAt = row.created_at ? String(row.created_at).slice(0, 16).replace('T', ' ') : '';
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px',
      marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)',
    }}>
      <DetailRow label="급여유형">{row.pay_type === 'hourly' ? '시급' : '월급'}</DetailRow>
      <DetailRow label="기본급">{fmt(row.base_salary)}원</DetailRow>
      <DetailRow label="부양가족">{row.dependents_count ?? 1}명</DetailRow>
      <DetailRow label="4대보험">
        국민연금 {yn(row.national_pension_yn)} · 건강보험 {yn(row.health_insurance_yn)} · 고용보험 {yn(row.employment_ins_yn)}
      </DetailRow>
      <DetailRow label="수습">{probation}</DetailRow>
      <DetailRow label="지급항목">
        {items.length === 0 ? '없음' : items.map((it, i) => (
          <div key={it.pay_item_id ?? i} style={{ color: it.is_enabled === false ? 'var(--text-muted)' : 'var(--text)' }}>
            {it.item_name || '항목'} {fmt(it.amount)}원{it.is_enabled === false ? ' (제외)' : ''}
          </div>
        ))}
      </DetailRow>
      <DetailRow label="변경사유">{row.reason || '-'}</DetailRow>
      <DetailRow label="작성자">
        {(row.created_by_name || row.user_name || '-')}{createdAt ? ` · ${createdAt}` : ''}
      </DetailRow>
    </div>
  );
}

export default function PayrollProfileHistoryPanel({
  form,
  setForm,
  editId,
  profileHistory = [],
  onLoadHistory,
  onDeleteHistory,
}) {
  const today = todayKST();
  const [expandedId, setExpandedId] = useState(null);
  // 최신순 배열이므로 effective_from<=today 인 첫 항목이 "오늘 기준 최신 적용분"
  const currentAppliedId = profileHistory.find((r) => String(r.effective_from || '') <= today)?.id;

  return (
    <div style={sectionBox}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>급여정보 적용</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
            적용일이 오늘 이후면 현재 급여는 유지하고 예약 이력만 저장됩니다.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 10 }}>
        <div>
          <label style={labelStyle}>급여정보 적용일</label>
          <input
            style={inputStyle}
            type="date"
            value={form.payroll_effective_from || today}
            onChange={(e) => setForm({ ...form, payroll_effective_from: e.target.value })}
          />
        </div>
        <div>
          <label style={labelStyle}>변경 사유</label>
          <input
            style={inputStyle}
            value={form.payroll_change_reason || ''}
            onChange={(e) => setForm({ ...form, payroll_change_reason: e.target.value })}
            placeholder="예: 2026년 6월 근로계약 변경"
          />
        </div>
      </div>

      {editId && profileHistory.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)' }}>
            적용 이력 <span style={{ fontWeight: 600 }}>— 각 항목은 그 시점의 전체 급여정보입니다 (▼ 펼쳐 보기)</span>
          </div>
          {profileHistory.map((row) => {
            const future = String(row.effective_from || '') > today;
            const isCurrent = row.id === currentAppliedId;
            const expanded = expandedId === row.id;
            return (
              <div
                key={row.id}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: future ? '1px solid rgba(37,99,235,0.35)' : '1px solid var(--border)',
                  background: future ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)',
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    style={{ minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{expanded ? '▲' : '▼'}</span>
                      {rowTitle(row)}
                      {future && <span style={badgeStyle('#2563eb', 'rgba(37,99,235,0.12)')}>예약</span>}
                      {isCurrent && <span style={badgeStyle('#16a34a', 'rgba(22,163,74,0.12)')}>현재 적용</span>}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {rowSub(row)}
                    </div>
                  </button>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" style={btnMini} onClick={() => onLoadHistory?.(row)}>불러오기</button>
                    <button type="button" style={{ ...btnMini, color: '#dc2626' }} onClick={() => onDeleteHistory?.(row)}>
                      {future ? '예약 취소' : '삭제'}
                    </button>
                  </div>
                </div>
                {expanded && <HistoryDetail row={row} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
